<template>
  <div class="flex flex-col h-full relative">
    <transition :name="animation_name" duration="300">
      <div v-if="show_prompt" class="overflow-y-auto overflow-x-hidden flex flex-col-reverse justify-end h-[85%] min-h-[85%]">
        <div ref="boxContainer" class="overflow-y-auto overflow-x-hidden pt-6 pl-6 flex flex-col-reverse" @scroll="handleScroll">
          <div v-for="(item, index) in prompt_items[selectedItem]" :key="index">
            <PromptItem :item="item" />
          </div>
        </div>
      </div>
    </transition>

    <div class="bg-gradient-to-r from-[#2DA7D780] to-[#841FF480] w-full rounded-lg absolute bottom-2">
      <div class="bg-white relative top-[1px] pt-2 w-full h-full flex items-center pr-2">
        <TextInput
          v-model="search_term"
          :value="search_term"
          :validate-on-input="true"
          :no-border="true"
          label=""
          :classes="'w-full'"
          input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md  placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
          name="name"
          type="text"
          :placeholder="$t('Start typing')"
          @keydown.enter.prevent="handleClickAIButton" />
        <div
          :class="[
            { 'pointer-events-none opacity-40': !search_term && !loading_ai_response },
            { 'pointer-events-none': loading_ai_response }
          ]"
          class="ml-auto bg-gradient-to-r cursor-pointer hover:shadow-md hover:shadow-purple-500/25 whitespace-nowrap flex space-x-2 items-center px-4 py-1 to-[#2DA7D7] text-white rounded-lg from-[#841FF4]"
          @click="handleClickAIButton"
          @keydown.enter.prevent="handleClickAIButton">
          <div>
            <AIMenuIcon :class="[{ 'animate-spin': loading_ai_response }]" class="w-5 h-5" />
          </div>
          <div>{{ loading_ai_response ? $t("Hmm let me think...") : $t("Ask Revsetter AI") }}</div>
        </div>
      </div>
    </div>
    <div v-if="show_scroll_to_end" class="flex w-full absolute bottom-16 cursor-pointer" @click="scrollToEnd">
      <div class="mx-auto bg-white rounded-full p-1 border">
        <ChevronDown class="w-5 h-5" />
      </div>
    </div>
  </div>
</template>

<script>
import AIMenuIcon from "@/components/shared/icons/AIMenuIcon"

import TextInput from "@/components/shared/TextInput.vue"

import PromptItem from "./PromptItem"
import { ChevronDown } from "lucide-vue-next"
import dataService from "@/services/data.service"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

import OnboardCustomerSuccessIcon from "@/components/shared/icons/OnboardCustomerSuccessIcon"
import OnboardAccountManagementIcon from "@/components/shared/icons/OnboardAccountManagementIcon"
import OnboardSalesIcon from "@/components/shared/icons/OnboardSalesIcon"
import OnboardRevenueOperationsIcon from "@/components/shared/icons/OnboardRevenueOperationsIcon"
import OnboardManagementIcon from "@/components/shared/icons/OnboardManagementIcon"
import OnboardExecutiveLeadershipIcon from "@/components/shared/icons/OnboardExecutiveLeadershipIcon"

import OnboardUsageAndHealthIcon from "@/components/shared/icons/OnboardUsageAndHealthIcon"
import OnboardRenewalsIcon from "@/components/shared/icons/OnboardRenewalsIcon"
import OnboardSupportIcon from "@/components/shared/icons/OnboardSupportIcon"
import OnboardChurnPreventionIcon from "@/components/shared/icons/OnboardChurnPreventionIcon"
import OnboardPooledCSIcon from "@/components/shared/icons/OnboardPooledCSIcon"

export default {
  components: {
    PromptItem,
    AIMenuIcon,
    TextInput,
    ChevronDown,
    // eslint-disable-next-line vue/no-unused-components
    OnboardCustomerSuccessIcon,
    // eslint-disable-next-line vue/no-unused-components
    OnboardAccountManagementIcon,
    // eslint-disable-next-line vue/no-unused-components
    OnboardSalesIcon,
    // eslint-disable-next-line vue/no-unused-components
    OnboardRevenueOperationsIcon,
    // eslint-disable-next-line vue/no-unused-components
    OnboardManagementIcon,
    // eslint-disable-next-line vue/no-unused-components
    OnboardExecutiveLeadershipIcon,
    // eslint-disable-next-line vue/no-unused-components
    OnboardUsageAndHealthIcon,
    // eslint-disable-next-line vue/no-unused-components
    OnboardRenewalsIcon,
    // eslint-disable-next-line vue/no-unused-components
    OnboardSupportIcon,
    // eslint-disable-next-line vue/no-unused-components
    OnboardChurnPreventionIcon,
    // eslint-disable-next-line vue/no-unused-components
    OnboardPooledCSIcon
  },
  props: { selectedItem: null },
  emits: [],
  data() {
    return {
      show_scroll_to_end: false,
      animation_name: "slide-fade",
      show_prompt: true,
      loading_ai_response: false,
      search_term: "",
      prompt_items: { chatbot: [], onboarding: [], goals: [], suggestions: [], settings: [], notifications: [] },
      prompts_initialized: { chatbot: false, onboarding: false, goals: false, suggestions: false, settings: false, notifications: false },
      create_goal_commands_list: [
        {
          title: this.$t("Create a Goal"),
          items: [
            { label: this.$t("Create an Expansion goal") },
            { label: this.$t("Create a Churn Prevention Goal") },
            { label: this.$t("Create a Metric Goal") },
            { label: this.$t("Create an Activity Goal") },
            { label: this.$t("Create an Onboarding Goal") },
            { label: this.$t("Create an Adoption Goal") },
            { label: this.$t("Create a Forecast metric Goal") },
            { label: this.$t("Create an Sales Target Goal") },
            { label: this.$t("Create a Health Score Goal") },
            { label: this.$t("Create an QBR/EBR Goal") },
            { label: this.$t("Create a Retention Goal") },
            { label: this.$t("Create a Specific Goal") }
          ]
        }
      ],
      create_task_commands_list: [
        {
          title: this.$t("Create a Task"),
          items: [
            { label: this.$t("Create an Expansion Task") },
            { label: this.$t("Create a Churn Prevention Task") },
            { label: this.$t("Create a Metric Task") },
            { label: this.$t("Create an Activity Task") },
            { label: this.$t("Create an Onboarding Task") },
            { label: this.$t("Create an Adoption Task") },
            { label: this.$t("Create a Forecast metric Task") },
            { label: this.$t("Create an Sales Target Task") },
            { label: this.$t("Create a Health Score Task") },
            { label: this.$t("Create an QBR/EBR Task") },
            { label: this.$t("Create a Retention Task") },
            { label: this.$t("Create a Specific Task") }
          ]
        }
      ],

      create_plan_commands_list: [
        {
          title: this.$t("Create a Plan"),
          items: [
            { label: this.$t("Create an Expansion Plan") },
            { label: this.$t("Create a Churn Prevention Plan") },
            { label: this.$t("Create a Metric Plan") },
            { label: this.$t("Create an Activity Plan") },
            { label: this.$t("Create an Onboarding Plan") },
            { label: this.$t("Create an Adoption Plan") },
            { label: this.$t("Create a Forecast metric Plan") },
            { label: this.$t("Create an Sales Target Plan") },
            { label: this.$t("Create a Health Score Plan") },
            { label: this.$t("Create an QBR/EBR Plan") },
            { label: this.$t("Create a Retention Plan") },
            { label: this.$t("Create a Specific Plan") }
          ]
        }
      ],

      create_interaction_commands_list: [
        {
          title: this.$t("Create a Interaction"),
          items: [
            { label: this.$t("Create an Expansion Interaction") },
            { label: this.$t("Create a Churn Prevention Interaction") },
            { label: this.$t("Create a Metric Interaction") },
            { label: this.$t("Create an Activity Interaction") },
            { label: this.$t("Create an Onboarding Interaction") },
            { label: this.$t("Create an Adoption Interaction") },
            { label: this.$t("Create a Forecast metric Interaction") },
            { label: this.$t("Create an Sales Target Interaction") },
            { label: this.$t("Create a Health Score Interaction") },
            { label: this.$t("Create an QBR/EBR Interaction") },
            { label: this.$t("Create a Retention Interaction") },
            { label: this.$t("Create a Specific Interaction") }
          ]
        }
      ],
      initial_commands_list: [
        {
          title: "Analyze",
          items: [
            { label: this.$t("Analyze data"), id: 1 },
            { label: this.$t("Analyze news about a record"), id: 2 },
            { label: this.$t("Research a record"), id: 2 },
            { label: this.$t("Analyze a graph"), id: 3 },
            { label: this.$t("Analyze my Activity"), id: 4 }
          ]
        },
        {
          title: this.$t("Create"),
          items: [
            { label: this.$t("Create a Plan"), id: 5 },
            { label: this.$t("Create a Goal"), id: 6 },
            { label: this.$t("Create a Task"), id: 7 },
            { label: this.$t("Create a Interaction"), id: 8 },
            { label: this.$t("Create a Graph"), id: 9 }
          ]
        },
        {
          title: this.$t("Automate"),
          items: [
            { label: this.$t("Send an email to a contact"), id: 10 },
            { label: this.$t("Send a NPS survey to a contact"), id: 11 },
            { label: this.$t("Set up Slack alerts"), id: 12 }
          ]
        }
      ],

      initial_onboarding_list: [
        {
          title: this.$t("Onboarding"),
          items: [
            { label: this.$t("Tell me more so I can help you better"), id: 1, action: this.onboardingTellMeMoreClick },
            { label: this.$t("Learn more about how to use RevSetter"), id: 2 },
            { label: this.$t("Close chat and explore."), id: 2 }
          ]
        }
      ]
    }
  },
  watch: {
    selectedItem(prev_item, new_item) {
      this.show_scroll_to_end = false
      const items_list = Object.keys(this.prompt_items).map((p) => p)
      const prev_index = items_list.findIndex((item) => item == prev_item)
      const new_index = items_list.findIndex((item) => item == new_item)
      this.init()
      if (prev_index < new_index) {
        this.animation_name = "opp-slide-fade"
      } else {
        this.animation_name = "slide-fade"
      }
      this.show_prompt = false
      setTimeout(() => {
        this.show_prompt = true
      }, 100)

      //   setTimeout(()=>{
      //     this.scrollToEnd()
      //   },500)
    }
  },

  created() {
    this.register_listeners()
    this.init()
  },
  activated() {
    this.register_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },

  methods: {
    hanldeSelectOnboardingTeam(option_selected) {
      this.addDialogue({ sender: "user", text: option_selected, prevent_ai_reply: true })
      this.addDialogue({ sender: "ai", text: this.$t("Based on your selection, select any of your direct responsibilities.") }).then(() => {
        const selection_list = [
          {
            title: this.$t("Customer Usage & Health"),
            sub_title: this.$t(
              "Focus on driving adoption & usage of my company's products & services, and other activities related to ensuring great customer health."
            ),
            icon: OnboardUsageAndHealthIcon
          },
          {
            title: this.$t("Renewals"),
            sub_title: this.$t("Manage and close customer renewals. Measured on metrics like GRR, churn and similar."),
            icon: OnboardRenewalsIcon
          },
          {
            title: this.$t("Support"),
            sub_title: this.$t("Manage incoming support request and customer issues as my primary responsibility."),
            icon: OnboardSupportIcon
          },
          {
            title: this.$t("Churn Prevention"),
            sub_title: this.$t("Identify red flags, drive customer health and ensure churn is mitigated"),
            icon: OnboardChurnPreventionIcon
          },
          {
            title: this.$t("Pooled CS"),
            sub_title: this.$t("Oversee a pool of customers that are mainly managed through automation."),
            icon: OnboardPooledCSIcon
          }
        ]
        this.addFancySelectionList({ selection_list, confirm_button_action: this.handleOnboardingSelectionConfirm })
      })
    },

    handleOnboardingSelectionConfirm() {
      this.addDialogue({ sender: "ai", text: this.$t("All done. Thanks for providing") }).then(() => {
        this.addDialogue({ sender: "ai", text: this.$t("Please fill the following to better understand what this page is about") }).then(
          () => {
            const form_fields = [
              {
                type: "selection",
                label: this.$t("Are your customers in a specific industry/vertical?"),
                inline: false,
                divider: true,
                value: "no",
                options: ["yes", "no"],
                required: true
              },
              {
                type: "multi_select",
                show_if_last_field_has_value: "yes",
                label: this.$t("Which ones?"),
                inline: false,
                options: ["Technology", "Food/Agriculture", "Automotive", "Electronics"],
                required: true
              },
              {
                type: "multi_switch_select",
                switches_inline: true,
                label: this.$t("Are your customers in a specific segment?"),
                inline: false,
                switches: [
                  { label: this.$t("SMB"), value: true },
                  { label: this.$t("Mid-sized"), value: true },
                  { label: this.$t("Large/Enterprise"), value: true }
                ],
                required: true,
                divider: true
              },
              {
                type: "selection",
                label: this.$t("How many customers do you directly manage?"),
                inline: false,
                divider: true,
                value: "0-20",
                options: ["0-20", "20-50", "50-100", "150-300", "300+"],
                required: true
              },
              {
                type: "multi_switch_select",
                switches_inline: true,
                label: this.$t("My main target/quota is..."),
                inline: false,
                switches: [
                  { label: this.$t("Monthly"), value: false },
                  { label: this.$t("Quarterly"), value: false },
                  { label: this.$t("Annual"), value: false }
                ],
                required: true,
                divider: true
              },
              {
                type: "multi_switch_select",
                switches_inline: false,
                label: this.$t("Who do you collaborate with most often?"),
                inline: false,
                switches: [
                  { label: this.$t("Admin"), value: false },
                  { label: this.$t("User 1"), value: false },
                  { label: this.$t("User 2"), value: false }
                ],
                required: false,
                divider: true
              }
            ]
            this.addFormFields({ form_fields, has_confirm_button: true, confirm_button_action: this.confirmOnboardingTemplateCreationForm })
          }
        )
      })
    },

    onboardingTellMeMoreClick() {
      this.addDialogue({ sender: "user", text: this.$t("Tell me more so I can help you better"), prevent_ai_reply: true })
      this.addDialogue({
        sender: "ai",
        text: this.$t(
          "It's a great idea to complete the onboarding. This will help me get better experience, automation, and better suited templates. For starters, please confirm the following details:"
        )
      }).then(() => {
        const form_fields = [
          { type: "text", value: "Ed Powers", label: "Full Name:", inline: true, required: true },
          { type: "text", value: "Excellence Partners", label: "Company:", inline: true, required: true },
          { type: "text", value: "Customers success", label: "Industry:", inline: true, required: false }
        ]
        this.addFormFields({
          form_fields,
          has_confirm_button: true,
          confirm_button_action: this.confirmOnboardingFirstStep,
          follow_up_text: "If there are any incorrect or missing details, modify the fields above."
        })
      })
    },

    confirmOnboardingTemplateCreationForm() {
      this.addDialogue({
        sender: "ai",
        text: this.$t("Generating new layouts, metrics, customer journeys and forecasting objects based on your answers"),
        action_performed: true
      })
    },

    confirmOnboardingFirstStep() {
      this.addDialogue({ sender: "ai", text: this.$t("Great, let's move on. What Team are you part of at Excellence Partners?") }).then(
        () => {
          const boxes_list = [
            {
              icon: OnboardCustomerSuccessIcon,
              title: "Customer Success",
              text: this.$t("(e.g. Customer Success Manager, Sr. CSM, Customer Support Rep., CS Rep. etc)"),
              action: this.hanldeSelectOnboardingTeam
            },
            {
              icon: OnboardAccountManagementIcon,
              title: "Account Management",
              text: this.$t("(Account Manager, Sr. AM, Renewal Manager, Expansion Manager, Technical AM etc)"),
              action: this.hanldeSelectOnboardingTeam
            },
            {
              icon: OnboardSalesIcon,
              title: "Sales",
              text: this.$t("(Account Executive, Sales Rep., Enterprise AE, Strategic Accounts, BDR, SDR, etc)"),
              action: this.hanldeSelectOnboardingTeam
            },
            {
              icon: OnboardRevenueOperationsIcon,
              title: "Revenue Operations",
              text: this.$t("(e.g. RevOps leader, Head of RevOps)"),
              action: this.hanldeSelectOnboardingTeam
            },
            {
              icon: OnboardManagementIcon,
              title: "Management",
              text: this.$t("(e.g. Director/VP of CS, Director/VP of AM, Director/VP of Sales etc)"),
              action: this.hanldeSelectOnboardingTeam
            },
            {
              icon: OnboardExecutiveLeadershipIcon,
              title: "Executive Leadership",
              text: this.$t("(e.g. Chief Customer Officer, Chief Revenue Officer, Chief Operating Officer etc)"),
              action: this.hanldeSelectOnboardingTeam
            }
          ]
          this.addBoxesSelection({ boxes_list, multi_select: false })
        }
      )
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.GOAL_UPDATED_IN_AI_PROMPT, (goal) => {
        this.prompt_items[this.selectedItem].forEach((item) => {
          if (item.meta?.attached_item?.id == goal.id) {
            item.meta.attached_item["data"] = goal
          }
        })
      })
      this.$emitter.on(ALL_EMITTERS.TASK_UPDATED_IN_AI_PROMPT, (task) => {
        this.prompt_items[this.selectedItem].forEach((item) => {
          if (item.meta?.attached_item?.id == task.id) {
            item.meta.attached_item["data"] = task
          }
        })
      })
      this.$emitter.on(ALL_EMITTERS.PLAN_UPDATED_IN_AI_PROMPT, (plan) => {
        this.prompt_items[this.selectedItem].forEach((item) => {
          if (item.meta?.attached_item?.id == plan.id) {
            item.meta.attached_item["data"] = plan
          }
        })
      })
      this.$emitter.on(ALL_EMITTERS.INTERACTION_UPDATED_IN_AI_PROMPT, (interaction) => {
        this.prompt_items[this.selectedItem].forEach((item) => {
          if (item.meta?.attached_item?.id == interaction.id) {
            item.meta.attached_item["data"] = interaction
          }
        })
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.GOAL_UPDATED_IN_AI_PROMPT)
      this.$emitter.off(ALL_EMITTERS.TASK_UPDATED_IN_AI_PROMPT)
      this.$emitter.off(ALL_EMITTERS.INTERACTION_UPDATED_IN_AI_PROMPT)
      this.$emitter.off(ALL_EMITTERS.PLAN_UPDATED_IN_AI_PROMPT)
    },

    handleScroll(e) {
      if (e.target.scrollTop) {
        this.show_scroll_to_end = true
      } else {
        this.show_scroll_to_end = false
      }
    },
    init() {
      if (!this.prompts_initialized[this.selectedItem]) {
        this.prompts_initialized[this.selectedItem] = true

        if (this.selectedItem == "onboarding") {
          this.addCommandList({ commands_list: this.$utils.clone(this.initial_onboarding_list) })
        }

        if (this.selectedItem == "chatbot") {
          this.prompt_items[this.selectedItem] = []
          this.addTitle({
            title_text: this.$t("Hello") + ` ${this.$store.state.auth.user.first_name},`,
            sub_title_text: this.$t("Select a quick topic, or type in any question you have for me.")
          }).then(() => {
            this.addCommandList({ commands_list: this.$utils.clone(this.initial_commands_list) }).then(() => {
              //DONE
            })
          })
        }

        if (this.selectedItem == "goals") {
          this.addCommandList({ commands_list: this.$utils.clone(this.initial_commands_list) }).then(() => {
            this.addTitle({
              title_text: `Hello ${this.$store.state.auth.user.first_name},`,
              sub_title_text: "Select a quick topic, or type in any question you have for me."
            })
          })
        }
      }
    },

    async addTitle({ title_text, sub_title_text }) {
      return new Promise((resolve) => {
        this.prompt_items[this.selectedItem].unshift({ type: "title", meta: { title_text: "", sub_title_text: "" } })
        const index = 0
        const title_promises = new Promise((resolve) => {
          title_text.split("").forEach((title_char, i) => {
            setTimeout(() => {
              this.prompt_items[this.selectedItem][index].meta.title_text += title_char
              if (i === title_text.length - 1) {
                resolve()
              }
            }, i * 30)
          })
        })

        const sub_title_promises = new Promise((resolve) => {
          sub_title_text.split("").forEach((sub_title_char, i) => {
            setTimeout(
              () => {
                this.prompt_items[this.selectedItem][index].meta.sub_title_text += sub_title_char
                if (i === sub_title_text.length - 1) {
                  resolve()
                }
              },
              title_text.length * 50 + i * 10
            )
          })
        })

        Promise.all([title_promises, sub_title_promises]).then(() => {
          resolve()
        })
      })
    },

    async addBoxesSelection({ boxes_list, multi_select }) {
      this.prompt_items[this.selectedItem].unshift({ type: "selection_boxes", meta: { selection_list: boxes_list, multi_select } })
      const index = 0

      return new Promise((resolve) => {
        boxes_list.forEach((box, box_index) => {
          setTimeout(() => {
            this.prompt_items[this.selectedItem][index].meta.selection_list[box_index]["show_box"] = true
            if (box_index == boxes_list.length - 1) {
              resolve()
            }
          }, 200 * box_index)
        })
      })
    },

    async addFancySelectionList({ selection_list, confirm_button_action }) {
      this.prompt_items[this.selectedItem].unshift({ type: "fancy_selection_list", meta: { selection_list, confirm_button_action } })
      const index = 0
      return new Promise((resolve) => {
        selection_list.forEach((box, selection_index) => {
          setTimeout(() => {
            this.prompt_items[this.selectedItem][index].meta.selection_list[selection_index]["show_selection_item"] = true
            if (selection_index == selection_list.length - 1) {
              this.prompt_items[this.selectedItem][index].meta["show_confirm_button"] = true
              resolve()
            }
          }, 200 * selection_index)
        })
      })
    },

    async addCommandList({ commands_list }) {
      return new Promise((resolve) => {
        this.prompt_items[this.selectedItem].unshift({ type: "commands_list", meta: { commands: [] } })
        const index = 0
        commands_list.forEach((command) => {
          this.prompt_items[this.selectedItem][index].meta.commands.push({
            title: "",
            items: this.$utils.clone(
              command.items.map((item) => {
                return { ...item, label: item.label.slice(0, -2) + "__" }
              })
            )
          })
        })

        const promises_list_1 = []

        let command_index_with_most_items = 0
        commands_list.forEach((command, command_i) => {
          if (command.items.length > commands_list[command_index_with_most_items].items.length) {
            command_index_with_most_items = command_i
          }
          const promise = new Promise((resolve) => {
            command.title.split("").forEach((title_char, i) => {
              setTimeout(() => {
                this.prompt_items[this.selectedItem][index].meta.commands[command_i]["title"] += title_char

                if (command_i == commands_list.length - 1 && i == commands_list[command_i].title.split("").length - 1) {
                  resolve()
                }
              }, i * 50)
            })
          })
          if (command_i == commands_list.length - 1) {
            promises_list_1.push(promise)
          }
        })

        Promise.all(promises_list_1).then(() => {
          commands_list.forEach((command, command_i) => {
            command.items.forEach((command_item, item_i) => {
              //eslint-disable-next-line max-nested-callbacks
              setTimeout(() => {
                this.prompt_items[this.selectedItem][index].meta.commands[command_i]["items"][item_i] = command_item
                if (command_i == command_index_with_most_items && item_i == command.items.length - 1) {
                  resolve()
                }
              }, 200 * item_i)
            })
          })
        })
      })
    },

    async addFormFields({ form_fields, has_confirm_button, confirm_button_action, follow_up_text }) {
      this.prompt_items[this.selectedItem].unshift({
        type: "form",
        meta: { fields: form_fields, has_confirm_button: has_confirm_button, follow_up_text: "", confirm_button_action }
      })
      const index = 0
      return new Promise((resolve) => {
        new Promise((resolve) => {
          form_fields.forEach((field, field_ind) => {
            setTimeout(() => {
              this.prompt_items[this.selectedItem][index].meta.fields[field_ind]["show_label"] = true
              setTimeout(() => {
                this.prompt_items[this.selectedItem][index].meta.fields[field_ind]["show_value"] = true
              }, 200)

              if (field_ind == form_fields.length - 1) {
                setTimeout(
                  () => {
                    resolve()
                  },
                  has_confirm_button ? 200 : 600
                )
              }
            }, field_ind * 300)
          })
        }).then(() => {
          new Promise((resolve) => {
            if (has_confirm_button) {
              setTimeout(() => {
                this.prompt_items[this.selectedItem][index].meta["show_confirm_button"] = true
                setTimeout(() => {
                  resolve()
                }, 400)
              }, 400)
            } else {
              setTimeout(() => {
                resolve()
              }, 600)
            }
          }).then(() => {
            if (follow_up_text) {
              follow_up_text?.split("").forEach((char, char_i) => {
                setTimeout(() => {
                  this.prompt_items[this.selectedItem][index].meta["follow_up_text"] += char
                }, char_i * 10)
                if (char_i == follow_up_text.split("").length - 1) {
                  resolve()
                }
              })
            } else {
              resolve()
            }
          })
        })
      })
    },

    scrollToEnd() {
      const box_container = this.$refs?.boxContainer
      setTimeout(() => {
        box_container.scrollTop += box_container?.scrollHeight + 20 - (box_container?.scrollTop + box_container?.offsetHeight)
      }, 50)
    },
    addDialogue({ sender, text, action_performed, attached_item, follow_up_text, ai_response, prevent_ai_reply }) {
      if (sender == "user" || (sender == "ai" && !ai_response)) {
        this.prompt_items[this.selectedItem].unshift({ type: "dialogue", meta: { text: "", sender: sender } })
      }
      const index = 0

      if (sender == "user") {
        this.prompt_items[this.selectedItem][index].meta.text = text
        // this.scrollToEnd()

        //         this.loading_ai_response = true
        //   this.search_term = ""
        //   setTimeout(() => {
        //     this.loading_ai_response = false
        //     this.addDialogue({sender:"ai", text: "whats this stupid question? Are you a human or a dog?"})
        //   }, 2000)

        if (text == "create goal") {
          this.addCommandList({ commands_list: this.$utils.clone(this.create_goal_commands_list) }).then(() => {
            this.addDialogue({
              sender: "ai",
              text: this.$t(
                "To create a goal, you can either select any of the templates from above, or create a new one from scratch, typing in the input field below."
              )
            })
          })
        } else if (text == "create task") {
          this.addCommandList({ commands_list: this.$utils.clone(this.create_task_commands_list) }).then(() => {
            this.addDialogue({
              sender: "ai",
              text: this.$t(
                "To create a task, you can either select any of the templates from above, or create a new one from scratch, typing in the input field below."
              )
            })
          })
        } else if (text == "create plan") {
          this.addCommandList({ commands_list: this.$utils.clone(this.create_plan_commands_list) }).then(() => {
            this.addDialogue({
              sender: "ai",
              text: this.$t(
                "To create a plan, you can either select any of the templates from above, or create a new one from scratch, typing in the input field below."
              )
            })
          })
        } else if (text == "create interaction") {
          this.addCommandList({ commands_list: this.$utils.clone(this.create_interaction_commands_list) }).then(() => {
            this.addDialogue({
              sender: "ai",
              text: this.$t(
                "To create an interaction, you can either select any of the templates from above, or create a new one from scratch, typing in the input field below."
              )
            })
          })
        } else if (!prevent_ai_reply) {
          this.addDialogue({ sender: "ai", text: "" })
          this.loading_ai_response = true
          setTimeout(() => {
            this.loading_ai_response = false
            const obj_calc = { sender: "ai", ai_response: true }

            if (this.prompt_items[this.selectedItem][1].meta.text.includes("goal")) {
              obj_calc["action_performed"] = true
              obj_calc["text"] = "Goal Achieve 1M ARR was created for Oracle company."

              obj_calc["attached_item"] = { type: "goal", id: "0cd1115a-b7d5-4b2f-a353-a74e0cd82e0e" }
              obj_calc["follow_up_text"] = "What else can I help you with?"
            } else if (this.prompt_items[this.selectedItem][1].meta.text.includes("task")) {
              obj_calc["action_performed"] = true
              obj_calc["text"] = "Task Achieve 1M ARR was created for Oracle company."

              obj_calc["attached_item"] = { type: "task", id: "fecb1c80-fd34-4c36-b20b-afb8642f14f0" }
              obj_calc["follow_up_text"] = "What else can I help you with?"
            } else if (this.prompt_items[this.selectedItem][1].meta.text.includes("plan")) {
              obj_calc["action_performed"] = true
              obj_calc["text"] = "Plan Achieve 1M ARR was created for Oracle company."

              obj_calc["attached_item"] = { type: "plan", id: "ac737af6-4f82-465c-985c-36e6bf81b0a2" }
              obj_calc["follow_up_text"] = "What else can I help you with?"
            } else if (this.prompt_items[this.selectedItem][1].meta.text.includes("interaction")) {
              obj_calc["action_performed"] = true
              obj_calc["text"] = "Interaction of type Call was created for Oracle company."

              obj_calc["attached_item"] = { type: "interaction", id: "14d20e4e-ba84-44cf-b078-ef6add28ed3f" }
              obj_calc["follow_up_text"] = "What else can I help you with?"
            } else {
              obj_calc["text"] = "Hi I am Revsetter AI. Currently not alive yet, these are just static prompts!"
            }
            this.addDialogue(obj_calc)
          }, 2000)
        }
      }

      if (sender == "ai") {
        // this.scrollToEnd()

        return new Promise((resolve) => {
          const index = 0
          this.prompt_items[this.selectedItem][index].meta["action_performed"] = action_performed
          this.prompt_items[this.selectedItem][index].meta["attached_item"] = attached_item
          text.split("").forEach((title_char, i) => {
            setTimeout(() => {
              this.prompt_items[this.selectedItem][index].meta.text += title_char
              const prompt_item_meta = this.prompt_items[this.selectedItem][index].meta

              if (i === text.length - 1) {
                const follow_up_text_clone = follow_up_text

                if (follow_up_text_clone) {
                  prompt_item_meta["follow_up_text"] = ""

                  if (prompt_item_meta.attached_item) {
                    dataService[`get${this.$utils.capitalize(prompt_item_meta.attached_item.type)}`](prompt_item_meta.attached_item.id)
                      //eslint-disable-next-line
                      .then((res) => {
                        prompt_item_meta.attached_item["data"] = res.data
                        prompt_item_meta.attached_item["show"] = true
                        follow_up_text_clone.split("").forEach(
                          //eslint-disable-next-line
                          (f_char, f_i) => {
                            setTimeout(
                              //eslint-disable-next-line
                              () => {
                                prompt_item_meta["follow_up_text"] += f_char
                                if (f_i == follow_up_text_clone.split("").length - 1) {
                                  resolve()
                                }
                              },
                              600 + f_i * 20
                            )
                          }
                        )
                      })
                  } else {
                    follow_up_text_clone.split("").forEach((f_char, f_i) => {
                      setTimeout(
                        //eslint-disable-next-line
                        () => {
                          prompt_item_meta["follow_up_text"] += f_char
                          if (f_i == follow_up_text_clone.split("").length - 1) {
                            resolve()
                          }
                        },
                        600 + f_i * 20
                      )
                    })
                  }
                } else {
                  if (prompt_item_meta.attached_item) {
                    dataService[[`get${this.$utils.capitalize(prompt_item_meta.attached_item.type)}`]](
                      prompt_item_meta.attached_item.id
                    ).then((res) => {
                      prompt_item_meta.attached_item["data"] = res.data
                      prompt_item_meta.attached_item["show"] = true
                      resolve()
                    })
                  } else {
                    resolve()
                  }
                }
              }
            }, i * 10)
          })
        })
      }
    },

    handleClickAIButton() {
      if (this.search_term) {
        this.scrollToEnd()
        this.addDialogue({ sender: "user", text: this.search_term })
        this.search_term = ""
      }
    }
  }
}
</script>

<style scoped>
.slide-fade-enter-active {
  transition: all 0.2s ease-out;
  opacity: 100;
}

.slide-fade-leave-active {
  transition: all 0.4s;
  opacity: 0;
}

.slide-fade-enter-from {
  transform: translateY(200px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateY(-200px);
  opacity: 0;
}

.opp-slide-fade-enter-active {
  transition: all 0.2s ease-out;
  opacity: 100;
}

.opp-slide-fade-leave-active {
  transition: all 0.4s;
  opacity: 0;
}

.opp-slide-fade-enter-from {
  transform: translateY(-200px);
  opacity: 0;
}
.opp-slide-fade-leave-to {
  transform: translateY(200px);
  opacity: 0;
}
</style>
